import { memo, ReactNode, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import CategoryLoader from './CategoryLoader'
import ProductCard from './ProductCard'
import ProductCardSkeleton from './ProductCardSkeleton'

import ExternalContent from '@/components/common/ExternalContent'
import { H3, P } from '@/components/common/Typo'
import useProductImageState from '@/hooks/useProductImageState'
import { cn } from 'oddle-shop-monorepo.design-system'

import DOMPurify from 'isomorphic-dompurify'

interface CategoryProps {
  isCategoryLoaded: boolean
  isCategoryDisplayed: boolean
  categoryVisibilityEmitter: {
    on: (event: string, callback: () => void) => void
    off: (event: string, callback: () => void) => void
    once: (event: string, callback: () => void) => void
    emit: (
      event: string,
      {
        isVisible,
        category,
      }: {
        isVisible: boolean
        category: Oms.MenuCategory
      }
    ) => boolean
  }
  category: Oms.MenuCategory
  showHorizontalNav: boolean
  showSideNav: boolean
  showCategoryDetails: (categoryId: string) => void
  registerCategoryInViewport: (categoryId: string) => void
  unregisterCategoryFromViewport: (categoryId: string) => void
  index: number
}

const Category = ({
  isCategoryLoaded,
  isCategoryDisplayed,
  categoryVisibilityEmitter,
  category,
  showHorizontalNav,
  showSideNav,
  showCategoryDetails,
  registerCategoryInViewport,
  unregisterCategoryFromViewport,
  index,
}: CategoryProps): ReactNode => {
  const { listView } = useProductImageState()
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '0% 0px -75% 0px', // More reasonable viewport for detection
  })

  useEffect(() => {
    categoryVisibilityEmitter.emit('visibilityChange', {
      isVisible: inView,
      category,
    })

    // Handle viewport-based lazy loading with 500ms delay
    if (inView) {
      registerCategoryInViewport(category.id)
    } else {
      unregisterCategoryFromViewport(category.id)
    }
  }, [
    inView,
    category,
    categoryVisibilityEmitter,
    registerCategoryInViewport,
    unregisterCategoryFromViewport,
  ])

  return (
    <div ref={ref} className="cat-viewport" id={`category-${index}`}>
      <CategoryLoader
        categoryId={category.id}
        showCategoryDetails={showCategoryDetails}
        disabled={isCategoryLoaded || !isCategoryDisplayed}
      >
        <a
          id={category.id}
          className={cn(
            'invisible relative -top-[60px] block',
            showHorizontalNav ? '-top-[150px]' : '-top-[80px]'
          )}
        />
        {isCategoryDisplayed && (
          <>
            <H3
              className="text-section-headings mx-1 my-5 text-3xl font-bold"
              sectionHeading
            >
              {category.name}
            </H3>
            <ExternalContent>
              <P
                className="text-section-headings mx-1"
                dangerouslySetInnerHTML={{
                  __html:
                    DOMPurify && typeof DOMPurify.sanitize === 'function'
                      ? DOMPurify.sanitize(category.description)
                      : category.description,
                }}
              />
            </ExternalContent>
            <div className="flex flex-wrap">
              {isCategoryLoaded
                ? category.items.map((product, idx) => (
                    <ProductCard
                      key={idx}
                      showSideNav={showSideNav}
                      category={category}
                      product={product}
                    />
                  ))
                : (category.items || []).map((_, idx) => (
                    <ProductCardSkeleton
                      categoryId={category.id}
                      showCategoryDetails={showCategoryDetails}
                      key={idx}
                      listView={listView}
                      showSideNav={showSideNav}
                    />
                  ))}
            </div>
          </>
        )}
      </CategoryLoader>
    </div>
  )
}

export default memo(Category)
